import React, { useState, useEffect } from 'react';
import { Package, Users, FileText } from 'lucide-react';
import { productService } from '../../services/productService';
import { quoteService } from '../../services/quoteService';
import { clientService } from '../../services/clientService';

const DashboardStats = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalQuotes: 0,
    totalCustomers: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let statsTimeout: NodeJS.Timeout;

    const loadStats = async () => {
      try {
        // Agregar timeout general para evitar carga infinita
        statsTimeout = setTimeout(() => {
          if (isMounted) {
            console.warn('Timeout en carga de estadísticas del dashboard');
            setIsLoading(false);
          }
        }, 8000); // Reducido a 8 segundos

        // Crear timeouts individuales para cada servicio
        const productsTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout en getProduct')), 3000);
        });
        
        const quotesTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout en getQuoteStats')), 3000);
        });
        
        const customersTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout en getCustomerStats')), 3000);
        });

        // Ejecutar todas las consultas con sus respectivos timeouts
        const [products, quoteStats, customerStats] = await Promise.allSettled([
          Promise.race([productService.getProducts(), productsTimeout]),
          Promise.race([quoteService.getQuoteStats(), quotesTimeout]),
          Promise.race([clientService.getCustomerStats(), customersTimeout])
        ]);

        // Limpiar timeout si todo fue exitoso
        clearTimeout(statsTimeout);

        if (isMounted) {
          setStats({
            totalProducts: products.status === 'fulfilled' ? (products.value as any[]).length : 0,
            totalQuotes: quoteStats.status === 'fulfilled' ? (quoteStats.value as any).total : 0,
            totalCustomers: customerStats.status === 'fulfilled' ? (customerStats.value as any).total : 0
          });
        }
      } catch (error) {
        console.error('Error al cargar estadísticas:', error);
        clearTimeout(statsTimeout);
        if (isMounted) {
          // En caso de error, mostrar valores por defecto
          setStats({
            totalProducts: 0,
            totalQuotes: 0,
            totalCustomers: 0
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      isMounted = false;
      clearTimeout(statsTimeout);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
            <div className="animate-pulse">
              <div className="flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg"></div>
                <div className="ml-3 sm:ml-4 flex-1">
                  <div className="h-3 sm:h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-6 sm:h-8 bg-gray-200 rounded w-12 sm:w-16"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {/* Tarjeta de Productos */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>
          <div className="ml-3 sm:ml-4">
            <p className="text-xs sm:text-sm text-gray-500">Total Productos</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
          </div>
        </div>
      </div>

      {/* Tarjeta de Cotizaciones */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
          </div>
          <div className="ml-3 sm:ml-4">
            <p className="text-xs sm:text-sm text-gray-500">Total Cotizaciones</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalQuotes}</p>
          </div>
        </div>
      </div>

      {/* Tarjeta de Clientes */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
          <div className="ml-3 sm:ml-4">
            <p className="text-xs sm:text-sm text-gray-500">Total Clientes</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;